// src/lib/cobrancaCliente.ts
//
// Acompanhamento de cobrança por cliente (CPF/CNPJ), não por fatura — ver
// migração 20260729100000_create_cobranca_clientes.sql. A atribuição de
// responsável só acontece por ação explícita de alguém (pop-up "Sim,
// assumir" ou Transferir, ver src/components/PainelNegociacaoCobranca.tsx)
// — nunca automaticamente só por o cliente entrar na esteira ou por alguém
// abrir o card pra olhar.

import { supabase } from './supabase';
import { CobrancaCliente } from '../../types';

export const normalizarCpfCnpj = (value?: string | null): string => {
  return String(value || '').replace(/\D/g, '');
};

/**
 * Resolve o CPF/CNPJ "canônico" do cliente de uma fatura: o cadastro da UC
 * tem prioridade sobre o valor extraído por OCR da fatura (mesmo princípio
 * já usado no cálculo do boleto — pagador cadastrado > titular da fatura).
 */
export const resolveCpfCnpjCliente = async (uc_codigo?: string | null, fallback?: string | null): Promise<string> => {
  if (uc_codigo) {
    const { data } = await supabase
      .from('unidades_consumidoras')
      .select('cpf_cnpj_cliente')
      .or(`numero_uc.eq.${uc_codigo},outro_numero_uc.eq.${uc_codigo}`)
      .limit(1)
      .maybeSingle();
    const fromUc = normalizarCpfCnpj(data?.cpf_cnpj_cliente);
    if (fromUc) return fromUc;
  }
  return normalizarCpfCnpj(fallback);
};

/**
 * Garante que exista uma linha em cobranca_clientes para o CPF/CNPJ informado.
 * Se já existir, não mexe em nada (carteira fixa/"sticky" — quem já tem dono
 * continua com o mesmo). Se não existir, cria sem responsável (fica
 * "aguardando contato, não atribuído" até alguém confirmar que vai assumir).
 */
export const ensureCobrancaCliente = async (
  cpfCnpjCliente: string,
  clienteNome?: string | null
): Promise<CobrancaCliente | null> => {
  const cpfCnpj = normalizarCpfCnpj(cpfCnpjCliente);
  if (!cpfCnpj) return null;

  const { data: existente } = await supabase
    .from('cobranca_clientes')
    .select('*')
    .eq('cpf_cnpj_cliente', cpfCnpj)
    .maybeSingle();

  if (existente) return existente as CobrancaCliente;

  const { data: inserido, error } = await supabase
    .from('cobranca_clientes')
    .insert({
      cpf_cnpj_cliente: cpfCnpj,
      cliente_nome: clienteNome || null,
      responsavel_cobranca_id: null,
    })
    .select('*')
    .single();

  // Corrida entre duas faturas do mesmo cliente enviadas ao mesmo tempo:
  // o UNIQUE(cpf_cnpj_cliente) rejeita o segundo insert — busca a linha
  // que a outra chamada já criou.
  if (error?.code === '23505') {
    const { data: jaCriado } = await supabase
      .from('cobranca_clientes')
      .select('*')
      .eq('cpf_cnpj_cliente', cpfCnpj)
      .maybeSingle();
    return (jaCriado as CobrancaCliente) || null;
  }
  if (error) throw error;

  return inserido as CobrancaCliente;
};
